import { useState, useEffect, useCallback, type FC } from 'react'
import { useLocation } from 'react-router-dom'
import { Box, type SxProps, type Theme } from '@mui/material'

import { guidedTourClasses } from './classes.js'
import { type Tour } from './model.js'
import { DisplayTour } from './DisplayTour.js'
import { FocusBackdrop, type FocusBackdropDimensions } from './FocusBackdrop.js'

type Props = {
  active?: boolean
  documentScrollQuerySelector?: string
  initialDisplayDelay?: number
  sx?: SxProps<Theme>
  tours: Tour[]
  onTourCompleted?: (tour: Tour) => void
}

export const GuidedTour: FC<Props> = ({
  active,
  documentScrollQuerySelector,
  initialDisplayDelay = 500,
  sx,
  tours,
  onTourCompleted,
}) => {
  const location = useLocation()
  const [toursToDisplay, setToursToDisplay] = useState<Tour[]>([])
  const [currentTour] = toursToDisplay
  const [dimensions, setDimensions] = useState<FocusBackdropDimensions | null>()

  useEffect(() => {
    if (tours.length > 0) {
      setTimeout(() => setToursToDisplay(tours), initialDisplayDelay)
    } else {
      setDimensions(null)
    }
  }, [initialDisplayDelay, location.pathname, tours])

  const handleTourClose = useCallback(() => {
    if (currentTour) {
      onTourCompleted?.(currentTour)

      setToursToDisplay(toursToDisplay.filter((t) => t.name !== currentTour.name))
    }
  }, [currentTour, onTourCompleted, toursToDisplay])

  const handleFocusDimensionsChange = useCallback((nextDimensions: FocusBackdropDimensions) => {
    setDimensions(nextDimensions)
  }, [])

  if (!active) {
    return null
  }

  return (
    <Box className={guidedTourClasses.root} sx={sx}>
      {dimensions && <FocusBackdrop dimensions={dimensions} />}
      {currentTour && (
        <DisplayTour
          documentScrollQuerySelector={documentScrollQuerySelector}
          key={currentTour.name}
          name={currentTour.name}
          steps={currentTour.steps}
          sx={currentTour.sx}
          onClose={handleTourClose}
          onFocusDimensionsChange={handleFocusDimensionsChange}
        />
      )}
    </Box>
  )
}
